<template>
  <div class="language-aware-editor">
    <!-- Language Tabs -->
    <v-card class="sticky-language-tabs" elevation="3">
      <div class="sticky-header-content">
        <v-tabs v-model="activeLang" color="primary" class="language-tabs">
          <v-tab
            v-for="lang in enabledLangs"
            :key="lang.code"
            :value="lang.code"
            class="language-tab"
          >
            <div class="d-flex align-center">
              <v-icon class="mr-2" size="small">mdi-translate</v-icon>
              <span class="language-name">{{ lang.label.toUpperCase() }}</span>
              <v-chip
                :color="getCompletionColor(lang.code)"
                size="x-small"
                class="ml-2"
              >
                {{ getCompletionPercentage(lang.code) }}%
              </v-chip>
            </div>
          </v-tab>
        </v-tabs>
      </div>
    </v-card>

    <!-- Editor -->
    <div v-if="isUserAuthorized && loaded" class="editor-content">
      <!-- Quick translate button (only show if EN is empty but PT has content) -->
      <div v-if="activeLang === 'en' && !content.trim() && hasPortugueseContent" class="mb-4">
        <v-alert type="info" variant="tonal" class="mb-2">
          English content is empty. Translate from Portuguese?
        </v-alert>
        <v-btn color="primary" @click="translateFromPortuguese" :loading="translating">
          <v-icon class="mr-2">mdi-translate</v-icon>
          Auto-translate from Portuguese
        </v-btn>
      </div>
      
      <!-- Save Button -->
      <div class="d-flex justify-end mb-4">
        <v-btn
          color="rgba(165, 216, 103, 1)"
          class="elevation-0 font-weight-bold"
          :loading="saving"
          :disabled="saving || !hasUnsavedChanges"
          @click="handleSave"
        >
          <v-icon class="mr-2">mdi-content-save</v-icon>
          Save Changes
        </v-btn>
      </div>
      
      <CustomQuill
        v-model="content"
        @text-change="onTextChange"
      />
    </div>
    <div v-else-if="loaded && !isUserAuthorized" class="read-only-content" v-html="content"></div>
    <div v-else class="loading-content">
      <v-progress-circular indeterminate size="28" class="mr-2" />
      Loading editor...
    </div>
  </div>
</template>

<script>
  import { ref, watch, computed, onMounted } from 'vue'
  import { useStore } from 'vuex'
  import db from '@/firebase/init'
  import firebase from 'firebase'
  import CustomQuill from '@/components/utilities/CustomQuill.vue'
  import { useLanguages } from '@/helpers/useLanguages'
  import Swal from 'sweetalert2' // Still needed for the unsaved changes dialog

  export default {
    name: 'LanguageAwareEditor',
    components: {
      CustomQuill,
    },
    props: {
      page: String,
      firestoreCollection: { type: String, required: true },
      allowedRoles: { type: Array, default: () => ['superAdmin'] },
      docId: String,
      initialData: Object,
    },
    emits: ['update:hasContent'],
    setup(props, { emit }) {
      const store = useStore()
      const { enabledLangs, defaultLang } = useLanguages()
      const content = ref('')
      const contentId = ref(props.docId || null)
      const timeout = ref(null)
      const loaded = ref(false)
      const activeLang = ref(defaultLang?.value || 'en')
      const localizedContent = ref({})
      const translating = ref(false)
      const saving = ref(false)
      const hasUnsavedChanges = ref(false)
      const lastSavedContent = ref({}) // Track last saved content per language

      const isUserAuthorized = computed(() => {
        // Get roles from Vuex store
        const userRoles = store.getters['UserData/getRoles'] || []
        return userRoles.some((r) => props.allowedRoles.includes(r))
      })

      // Get language-specific content
      const getLC = (lang) => {
        // Only create if it doesn't exist - don't overwrite loaded content
        if (!localizedContent.value[lang]) {
          localizedContent.value[lang] = {
            content: '',
          }
        }
        // Ensure content property exists
        if (!Object.prototype.hasOwnProperty.call(localizedContent.value[lang], 'content')) {
          localizedContent.value[lang].content = ''
        }
        return localizedContent.value[lang]
      }

      // Calculate completion percentage for a language
      const getCompletionPercentage = (lang) => {
        // Directly access localizedContent to avoid getLC creating empty objects
        const langData = localizedContent.value[lang]
        if (langData && langData.content && langData.content.trim().length > 0) {
          return 100
        }
        return 0
      }

      const getCompletionColor = (lang) => {
        const percentage = getCompletionPercentage(lang)
        if (percentage === 100) return 'success'
        if (percentage > 0) return 'warning'
        return 'grey'
      }

      // Check if Portuguese content exists
      const hasPortugueseContent = computed(() => {
        const ptContent = localizedContent.value.pt?.content
        const hasContent = ptContent && ptContent.trim().length > 0
        console.log('🔍 Checking Portuguese content:', hasContent, 'length:', ptContent?.length)
        return hasContent
      })

      // Translate Portuguese content to English
      const translateFromPortuguese = async () => {
        translating.value = true
        try {
          const ptContent = localizedContent.value.pt?.content || ''
          if (!ptContent.trim()) {
            alert('No Portuguese content found to translate')
            translating.value = false
            return
          }

          // Strip HTML tags for translation (Google Translate works better with plain text)
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = ptContent
          const plainText = tempDiv.textContent || tempDiv.innerText || ''
          
          // Check if text is too long (URL length limit is ~2000 chars)
          if (plainText.length > 1500) {
            alert('Content is too long for automatic translation. Please translate manually or use shorter content.')
            translating.value = false
            return
          }

          // Use Google Translate API (free tier via fetch)
          // Note: This is a simple approach. For production, you'd want to use the Cloud Translation API
          const translateUrl = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=pt&tl=en&dt=t&q=${encodeURIComponent(plainText)}`
          
          const response = await fetch(translateUrl)
          
          if (!response.ok) {
            throw new Error(`Translation API returned ${response.status}: ${response.statusText}`)
          }
          
          const data = await response.json()
          
          if (data && data[0] && Array.isArray(data[0])) {
            // Extract translated text from response
            const translatedText = data[0].map((item) => item[0] || '').join('')
            
            if (translatedText.trim()) {
            // Update English content
            if (!localizedContent.value.en) {
              localizedContent.value.en = { content: '' }
            }
            localizedContent.value.en.content = translatedText
            content.value = translatedText
            
            // Update unsaved changes flag
            lastSavedContent.value.en = '' // Reset since this is new content
            hasUnsavedChanges.value = true
            
            console.log('✅ Translated English content (ready to save)')
            
            // Show success toast
            Swal.fire({
              icon: 'info',
              title: 'Translation Complete!',
              text: 'English content has been translated. Click "Save Changes" to save it.',
              timer: 3000,
              showConfirmButton: false,
              toast: true,
              position: 'top',
              background: '#592892', // Brand purple
              color: '#fff',
              iconColor: '#fff',
            })
            } else {
              alert('Translation returned empty result. Please try again.')
            }
          } else {
            alert('Translation failed. Invalid response from translation service.')
          }
        } catch (error) {
          console.error('Translation error:', error)
          alert(`Translation failed: ${error.message}. The content may be too long. Please translate manually.`)
        } finally {
          translating.value = false
        }
      }

      // Load content from Firestore
      const loadContent = async () => {
        try {
          let pagePath = props.page || 'journeyorientation'
          
          // If we have a docId, load that document directly
          if (props.docId) {
            const doc = await db.collection(props.firestoreCollection).doc(props.docId).get()
            if (doc.exists) {
              const data = doc.data()
              contentId.value = doc.id
              
              console.log('📄 Loading document with docId:', data)
              
              // Load localizedContent structure
              if (data.localizedContent) {
                // Has new structure - use it
                localizedContent.value = JSON.parse(JSON.stringify(data.localizedContent))
                console.log('✅ Using localizedContent structure:', localizedContent.value)
                
                // Check if content is empty and migrate from legacy structure
                if (enabledLangs.value && enabledLangs.value.length > 0) {
                  enabledLangs.value.forEach((lang) => {
                    const langCode = lang.code
                    const langContent = localizedContent.value[langCode]
                    
                    // If localizedContent exists but content is empty, try to migrate from legacy
                    if (langContent && (!langContent.content || langContent.content.trim() === '')) {
                      // Try to get content from variants.default.{lang}
                      if (data.variants?.default?.[langCode]) {
                        langContent.content = data.variants.default[langCode]
                        console.log(`🔄 Migrated ${langCode} content from variants.default.${langCode}`)
                      }
                      // For English, also check top-level content field
                      else if (langCode === 'en' && data.content) {
                        langContent.content = data.content
                        console.log('🔄 Migrated English content from top-level content field')
                      }
                    }
                  })
                }
              } else if (data.content || data.variants?.default?.en) {
                // Migrate legacy content to localizedContent.en
                const legacyContent = data.content || data.variants?.default?.en || ''
                console.log('🔄 Migrating legacy content, length:', legacyContent.length)
                localizedContent.value = {
                  en: {
                    content: legacyContent,
                  },
                }
                // Also initialize other enabled languages with empty content
                if (enabledLangs.value && enabledLangs.value.length > 0) {
                  enabledLangs.value.forEach((lang) => {
                    if (lang.code !== 'en' && !localizedContent.value[lang.code]) {
                      localizedContent.value[lang.code] = { content: '' }
                    }
                  })
                }
              } else {
                // Initialize empty structure for all languages
                if (enabledLangs.value && enabledLangs.value.length > 0) {
                  enabledLangs.value.forEach((lang) => {
                    if (!localizedContent.value[lang.code]) {
                      localizedContent.value[lang.code] = { content: '' }
                    }
                  })
                }
              }
              
              // Set current language content - directly access to avoid getLC creating empty objects
              const langCode = activeLang.value
              const langData = localizedContent.value[langCode]
              
              console.log('🌍 Active lang:', langCode)
              console.log('📦 Raw localizedContent[langCode]:', langData)
              console.log('📦 Content value:', langData?.content)
              
              if (langData && langData.content !== undefined && langData.content !== null) {
                content.value = langData.content
                console.log('✅ Setting content from langData, length:', content.value.length)
              } else {
                content.value = ''
                console.log('⚠️ No content found for language:', langCode)
              }
              loaded.value = true
              emit('update:hasContent', !!content.value?.trim())
              return
            }
          }

          // Otherwise, search by page name
          const snapshot = await db.collection(props.firestoreCollection).get()
          let matchedDoc = null
          
          snapshot.forEach((doc) => {
            const data = doc.data()
            if (data.page === pagePath) {
              matchedDoc = { ...data, id: doc.id }
            }
          })

          if (!matchedDoc && snapshot.size > 0) {
            // Use first document as fallback
            matchedDoc = { ...snapshot.docs[0].data(), id: snapshot.docs[0].id }
          }

          if (matchedDoc) {
            contentId.value = matchedDoc.id
            console.log('📄 Loading document by page:', matchedDoc)

            // Load localizedContent structure
            if (matchedDoc.localizedContent) {
              // Has new structure - use it
              localizedContent.value = JSON.parse(JSON.stringify(matchedDoc.localizedContent))
              console.log('✅ Using localizedContent structure:', localizedContent.value)
              
              // Check if content is empty and migrate from legacy structure
              if (enabledLangs.value && enabledLangs.value.length > 0) {
                enabledLangs.value.forEach((lang) => {
                  const langCode = lang.code
                  const langContent = localizedContent.value[langCode]
                  
                  // If localizedContent exists but content is empty, try to migrate from legacy
                  if (langContent && (!langContent.content || langContent.content.trim() === '')) {
                    // Try to get content from variants.default.{lang}
                    if (matchedDoc.variants?.default?.[langCode]) {
                      langContent.content = matchedDoc.variants.default[langCode]
                      console.log(`🔄 Migrated ${langCode} content from variants.default.${langCode}`)
                    }
                    // For English, also check top-level content field
                    else if (langCode === 'en' && matchedDoc.content) {
                      langContent.content = matchedDoc.content
                      console.log('🔄 Migrated English content from top-level content field')
                    }
                  }
                })
              }
            } else if (matchedDoc.content || matchedDoc.variants?.default?.en) {
              // Migrate legacy content to localizedContent.en
              const legacyContent = matchedDoc.content || matchedDoc.variants?.default?.en || ''
              console.log('🔄 Migrating legacy content, length:', legacyContent.length)
              localizedContent.value = {
                en: {
                  content: legacyContent,
                },
              }
              // Also initialize other enabled languages with empty content
              if (enabledLangs.value && enabledLangs.value.length > 0) {
                enabledLangs.value.forEach((lang) => {
                  if (lang.code !== 'en' && !localizedContent.value[lang.code]) {
                    localizedContent.value[lang.code] = { content: '' }
                  }
                })
              }
            } else {
              // Initialize empty structure for all languages
              if (enabledLangs.value && enabledLangs.value.length > 0) {
                enabledLangs.value.forEach((lang) => {
                  if (!localizedContent.value[lang.code]) {
                    localizedContent.value[lang.code] = { content: '' }
                  }
                })
              }
            }

            // Set current language content - directly access to avoid getLC creating empty objects
            const langCode = activeLang.value
            const langData = localizedContent.value[langCode]
            
            console.log('🌍 Active lang:', langCode)
            console.log('📦 Raw localizedContent[langCode]:', langData)
            console.log('📦 Content value:', langData?.content)
            
            if (langData && langData.content !== undefined && langData.content !== null) {
              content.value = langData.content
              // Initialize last saved content for this language
              lastSavedContent.value[langCode] = langData.content
              console.log('✅ Setting content from langData, length:', content.value.length)
            } else {
              content.value = ''
              lastSavedContent.value[langCode] = ''
              console.log('⚠️ No content found for language:', langCode)
            }
            hasUnsavedChanges.value = false
            loaded.value = true
            emit('update:hasContent', !!content.value?.trim())
          } else {
            loaded.value = true
            emit('update:hasContent', false)
          }
        } catch (error) {
          console.error('Error loading content:', error)
          loaded.value = true
          emit('update:hasContent', false)
        }
      }

      // Save content to Firestore
      const save = async (contentData, languageOverride = null) => {
        const user = firebase.auth().currentUser
        if (!user || !isUserAuthorized.value) {
          return false
        }

        saving.value = true

        // Use languageOverride if provided (for saving when switching tabs), otherwise use activeLang
        const langToSave = languageOverride || activeLang.value
        const contentValue = typeof contentData === 'string' ? contentData : ''
        
        // Update localizedContent for the specified language
        if (!localizedContent.value[langToSave]) {
          localizedContent.value[langToSave] = { content: '' }
        }
        localizedContent.value[langToSave].content = contentValue

        // Prepare update data - save the entire localizedContent structure
        const updateData = {
          localizedContent: localizedContent.value,
          updated: Date.now(),
          // Remove autoTranslate flags since we're now using manual editing
          autoTranslate: false,
        }

        try {
          if (contentId.value) {
            await db
              .collection(props.firestoreCollection)
              .doc(contentId.value)
              .update(updateData)
            console.log(`💾 Saved content for language: ${langToSave}`)
          } else {
            // Create new document
            const pagePath = props.page || 'journeyorientation'
            const newDoc = {
              ...updateData,
              page: pagePath,
            }
            const docRef = await db
              .collection(props.firestoreCollection)
              .add(newDoc)
            contentId.value = docRef.id
            console.log('💾 Created new document:', docRef.id)
          }
          
          // Update last saved content for this language
          lastSavedContent.value[langToSave] = contentValue
          
          // Check if all current changes are saved
          const currentContent = localizedContent.value[langToSave]?.content || ''
          hasUnsavedChanges.value = currentContent !== lastSavedContent.value[langToSave]
          
          saving.value = false
          return true
        } catch (error) {
          console.error('Error saving content:', error)
          saving.value = false
          throw error
        }
      }

      // Handle manual save button click
      const handleSave = async () => {
        try {
          const success = await save(content.value)
          if (success) {
            const langLabel = enabledLangs.value.find(l => l.code === activeLang.value)?.label || 'Content'
            Swal.fire({
              icon: 'success',
              title: 'Saved!',
              text: `${langLabel} saved successfully.`,
              timer: 2000,
              showConfirmButton: false,
              toast: true,
              position: 'top',
              background: 'rgba(165, 216, 103, 1)', // Brand green
              color: '#fff',
              iconColor: '#fff',
            })
          }
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Save Failed',
            text: 'There was an error saving your changes. Please try again.',
            timer: 3000,
            showConfirmButton: false,
            toast: true,
            position: 'top',
            background: '#592892', // Brand purple
            color: '#fff',
            iconColor: '#fff',
          })
        }
      }

      const onTextChange = () => {
        // CustomQuill emits text-change without parameters, so we read from content.value
        // which is bound via v-model and updated by CustomQuill's update:modelValue
        const currentLang = activeLang.value
        const contentValue = content.value || ''
        
        // Ensure the language object exists
        if (!localizedContent.value[currentLang]) {
          localizedContent.value[currentLang] = { content: '' }
        }
        
        // Update localizedContent with the current editor content
        localizedContent.value[currentLang].content = contentValue
        
        // Check if there are unsaved changes
        const lastSaved = lastSavedContent.value[currentLang] || ''
        hasUnsavedChanges.value = contentValue !== lastSaved
        
        console.log(`📝 Text changed for ${currentLang}, hasUnsavedChanges:`, hasUnsavedChanges.value)
      }

      // Watch for language changes - update content when switching languages
      watch(activeLang, async (newLang, oldLang) => {
        if (loaded.value && newLang !== oldLang && oldLang) {
          console.log(`🔄 Language switch: ${oldLang} → ${newLang}`)
          
          // Cancel any pending save first
          if (timeout.value) {
            clearTimeout(timeout.value)
            timeout.value = null
          }
          
          // Ensure the old language object exists
          if (!localizedContent.value[oldLang]) {
            localizedContent.value[oldLang] = { content: '' }
          }
          
          // Sync current editor content to localizedContent (but don't auto-save)
          const editorContent = content.value || ''
          localizedContent.value[oldLang].content = editorContent
          
          // Update unsaved changes flag for old language
          const lastSaved = lastSavedContent.value[oldLang] || ''
          const hasChanges = editorContent !== lastSaved
          
          // Warn if there are unsaved changes
          if (hasChanges && editorContent.length > 0) {
            const shouldContinue = await Swal.fire({
              icon: 'warning',
              title: 'Unsaved Changes',
              text: `You have unsaved changes in ${enabledLangs.value.find(l => l.code === oldLang)?.label || oldLang.toUpperCase()}. Do you want to save before switching?`,
              showCancelButton: true,
              confirmButtonText: 'Save & Switch',
              cancelButtonText: 'Discard & Switch',
              confirmButtonColor: '#592892', // Brand purple for Save
              cancelButtonColor: '#a5d867', // Brand green for Discard
            })
            
            if (shouldContinue.isConfirmed) {
              // Save before switching
              try {
                await save(editorContent, oldLang)
                Swal.fire({
                  icon: 'success',
                  title: 'Saved!',
                  text: 'Changes saved successfully.',
                  timer: 1500,
                  showConfirmButton: false,
                  toast: true,
                  position: 'top',
                  background: 'rgba(165, 216, 103, 1)', // Brand green
                  color: '#fff',
                  iconColor: '#fff',
                })
              } catch (error) {
                console.error(`Error saving ${oldLang} content:`, error)
                Swal.fire({
                  icon: 'warning',
                  title: 'Save Failed',
                  text: 'Could not save changes. Switching anyway...',
                  timer: 2000,
                  showConfirmButton: false,
                  toast: true,
                  position: 'top',
                  background: '#592892', // Brand purple
                  color: '#fff',
                  iconColor: '#fff',
                })
              }
            } else {
              // Discard changes - revert to last saved
              localizedContent.value[oldLang].content = lastSaved
            }
          }
          
          // Load new language content directly from localizedContent
          const langData = localizedContent.value[newLang]
          if (langData && langData.content !== undefined && langData.content !== null && langData.content.length > 0) {
            content.value = langData.content
            // Update last saved content
            lastSavedContent.value[newLang] = langData.content
            hasUnsavedChanges.value = false
            console.log(`✅ Loaded content for ${newLang}, length:`, content.value.length)
          } else {
            // No content exists for this language
            content.value = ''
            lastSavedContent.value[newLang] = ''
            hasUnsavedChanges.value = false
            // Initialize empty content structure
            if (!localizedContent.value[newLang]) {
              localizedContent.value[newLang] = { content: '' }
            }
            console.log(`⚠️ No content found for ${newLang}, initialized empty`)
          }
        }
      })

      // Initialize component
      onMounted(async () => {
        // Wait a bit for enabledLangs to be available
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // Set default language (ensure it's 'en' if available)
        if (enabledLangs.value && enabledLangs.value.length > 0) {
          const defaultLangObj = enabledLangs.value.find((l) => l.isDefault)
          if (defaultLangObj) {
            activeLang.value = defaultLangObj.code
          } else {
            // Prefer 'en' if available, otherwise first language
            const enLang = enabledLangs.value.find((l) => l.code === 'en')
            activeLang.value = enLang ? enLang.code : enabledLangs.value[0].code
          }
        } else {
          // Fallback to 'en' if enabledLangs not loaded yet
          activeLang.value = 'en'
        }

        console.log('🚀 Initializing with activeLang:', activeLang.value)

        // Initialize localizedContent for all enabled languages
        if (enabledLangs.value && enabledLangs.value.length > 0) {
          enabledLangs.value.forEach((lang) => {
            if (!localizedContent.value[lang.code]) {
              localizedContent.value[lang.code] = { content: '' }
            }
          })
        } else {
          // Fallback: initialize at least 'en'
          if (!localizedContent.value.en) {
            localizedContent.value.en = { content: '' }
          }
        }

        // Load content
        await loadContent()
      })

      return {
        enabledLangs,
        activeLang,
        content,
        loaded,
        isUserAuthorized,
        getLC,
        getCompletionPercentage,
        getCompletionColor,
        onTextChange,
        hasPortugueseContent,
        translateFromPortuguese,
        translating,
        handleSave,
        saving,
        hasUnsavedChanges,
      }
    },
  }
</script>

<style scoped>
  .language-aware-editor {
    width: 100%;
  }

  .sticky-language-tabs {
    position: -webkit-sticky;
    position: sticky;
    top: 100px;
    z-index: 1000;
    background: white;
    border-bottom: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 100%;
    margin-bottom: 16px;
  }

  .sticky-header-content {
    display: flex;
    align-items: center;
    padding: 16px;
    min-height: 64px;
  }

  .language-tabs {
    flex: 1;
  }

  .editor-content {
    margin-top: 16px;
  }

  .read-only-content {
    margin-top: 16px;
    padding: 16px;
  }
</style>

